import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doSetupBillingAdminOrganisationSave,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import { BillingStatus } from 'types/Billing';
import { MonthEnum, Months } from 'types/types';
import { TextField } from 'components/Field/TextField';
import { CardRow } from 'components/CardRow';
import { theme } from 'lib/theme';
import { NumberSpinner } from 'components/Field/NumberSpinner';
import { Txt } from 'components/Txt';
import { View } from 'react-native';

export const BillingOrganisationSettingsModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsBillingOrganisation>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const {
    status,
    invoicePrefix,
    invoicePaymentTermsDays,
    hasFreeAssessments,
    assessmentInvoiceMonths,
  } = route.params;

  const initDataRef = React.useRef({
    status,
    invoicePrefix,
    invoicePaymentTermsDays,
    hasFreeAssessments,
    assessmentInvoiceMonths,
  });
  const dataRef = React.useRef(cloneDeep(initDataRef.current));
  const isDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(() => {
    dispatch(doSetupBillingAdminOrganisationSave({ changes: dataRef.current }));
  }, [dispatch]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () =>
          dispatch(
            NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingOrganisation]),
          ),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingOrganisation]));
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  const toggleMonth = (month: MonthEnum) => {
    if (dataRef.current.assessmentInvoiceMonths.includes(month)) {
      dataRef.current.assessmentInvoiceMonths =
        dataRef.current.assessmentInvoiceMonths.filter((m) => m !== month);
    } else {
      const newMonthArray = [...dataRef.current.assessmentInvoiceMonths, month];
      dataRef.current.assessmentInvoiceMonths = Months.filter((m) =>
        newMonthArray.includes(m.name),
      ).map((m) => m.name);
    }
    forceUpdate();
  };

  const isDirtyValue = isDirtyRef.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyValue });
  }, [navigation, isDirtyValue]);

  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Billing General"
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card label="Status" contentHorizontal>
        <CardRow
          label="Running"
          labelColor={theme.colors.green}
          style={{ flex: 1 }}
          radioValue={dataRef.current.status === BillingStatus.running}
          onPress={() =>
            handleChangeValue('status', BillingStatus.running, true)
          }
        />
        <CardRow
          label="Paused"
          labelColor={theme.colors.orange}
          style={{ flex: 1 }}
          radioValue={dataRef.current.status === BillingStatus.paused}
          onPress={() =>
            handleChangeValue('status', BillingStatus.paused, true)
          }
        />
      </Card>

      <View
        style={{
          flexDirection: 'row',
          flex: 1,
          alignItems: 'stretch',
        }}
      >
        <Card label="Invoice Prefix" margin={false} style={{ flex: 1 }} />
        <Card
          label="Payment Terms"
          margin={false}
          style={{ flex: 1 }}
          labelStyle={{ marginLeft: theme.paddingHorizontal }}
        />
      </View>
      <Card contentHorizontal>
        <TextField
          placeholder={dataRef.current.invoicePrefix}
          value={dataRef.current.invoicePrefix}
          onChange={(value) => handleChangeValue('invoicePrefix', value)}
          onEsc={handleClose}
          containerStyle={{
            flex: 1,
          }}
        />
        <CardRow
          style={{ flex: 1, paddingLeft: 0, paddingRight: 0 }}
          contentStyle={{
            paddingHorizontal: theme.paddingHorizontal,
            paddingVertical: 0,
          }}
          childrenStyle={{
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: theme.paddingHorizontal,
          }}
        >
          <NumberSpinner
            minValue={1}
            value={dataRef.current.invoicePaymentTermsDays}
            style={{ marginVertical: 0, marginTop: 0 }}
            onChange={(value) =>
              handleChangeValue('invoicePaymentTermsDays', value)
            }
          />
          <Txt>Days</Txt>
        </CardRow>
      </Card>

      <Card
        label="Paid or Free Assessments"
        contentHorizontal
        explanation={
          dataRef.current.hasFreeAssessments
            ? 'This organisation will not be charged for assessments they perform for themselves but will be charged for assessments they perform for other organisations'
            : ''
        }
      >
        <CardRow
          label="Paid"
          style={{ flex: 1 }}
          radioValue={!dataRef.current.hasFreeAssessments}
          onPress={() => handleChangeValue('hasFreeAssessments', false, true)}
        />
        <CardRow
          label="Free"
          style={{ flex: 1 }}
          radioValue={!!dataRef.current.hasFreeAssessments}
          onPress={() => handleChangeValue('hasFreeAssessments', true, true)}
        />
      </Card>

      <Card
        label="Assessment Invoice Months"
        explanation="The months the organisation will receive an invoice for assessments they perform"
        contentStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
      >
        {Months.map((m) => (
          <CardRow
            key={m.number}
            label={m.name}
            checkedValue={dataRef.current.assessmentInvoiceMonths.includes(
              m.name,
            )}
            onPress={() => toggleMonth(m.name)}
            style={{ width: '33.3%' }}
          />
        ))}
      </Card>
    </PageScrollView>
  );
};
